import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cibli/shared';
import { Loader2, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useCVList } from '@/features/cv/hooks/useCVList';

import CVMiniatureSelectable from './CVMiniatureSelectable';

interface SelectCVDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectCV: (cvId: string) => void;
  selectedCvId?: string | null;
}

export default function SelectCVDialog({
  open,
  onOpenChange,
  onSelectCV,
  selectedCvId,
}: SelectCVDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filteredCVs, isLoading, error, searchTerm, setSearchTerm } = useCVList();
  const [localSelectedCvId, setLocalSelectedCvId] = useState<string | null>(selectedCvId ?? null);

  useEffect(() => {
    if (open && selectedCvId) {
      setLocalSelectedCvId(selectedCvId);
    }
  }, [open, selectedCvId]);

  useEffect(() => {
    if (!open) {
      setLocalSelectedCvId(null);
    }
  }, [open]);

  const handleSelectCV = (cvId: string) => {
    setLocalSelectedCvId(prev => (prev === cvId ? null : cvId));
  };

  const handleConfirm = () => {
    if (localSelectedCvId) {
      onSelectCV(localSelectedCvId);
      onOpenChange(false);
    }
  };

  const handleCreateNewCV = () => {
    onOpenChange(false);
    navigate('/interview', { state: { autoStartWithSessionUser: true } });
  };

  const showEmptyState = !isLoading && filteredCVs.length === 0 && !searchTerm;
  const showNoResults = !isLoading && filteredCVs.length === 0 && searchTerm;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(560px,92vw)] max-h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-cj-green-dark/5">
          <DialogTitle className="font-display text-base text-cj-green-dark">
            {t('dashboard.selectCV.title')}
          </DialogTitle>
          <DialogDescription className="text-xs text-cj-green-dark/40">
            {t('dashboard.selectCV.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        {!showEmptyState && (
          <div className="px-6 py-3 border-b border-cj-green-dark/5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cj-green-dark/30" />
              <input
                type="text"
                placeholder={t('dashboard.selectCV.searchPlaceholder')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full bg-cj-green-light/10 rounded-xl pl-9 pr-4 py-2 text-sm text-cj-green-dark border border-transparent focus:border-cj-green focus:bg-white focus:outline-none transition placeholder:text-cj-green-dark/30"
              />
            </div>
          </div>
        )}

        {/* CV list — scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-cj-green" />
            </div>
          )}

          {error && (
            <div className="text-center py-10">
              <p className="text-sm text-cj-pink">{t('errors.internal_error')}</p>
            </div>
          )}

          {showEmptyState && (
            <div className="flex flex-col items-center justify-center py-10 px-4 text-center gap-4">
              <div className="w-12 h-12 bg-cj-green-light/30 rounded-2xl flex items-center justify-center">
                <Plus className="w-6 h-6 text-cj-green" />
              </div>
              <p className="text-sm text-cj-green-dark/50 max-w-xs">
                {t('dashboard.selectCV.noCV')}
              </p>
              <Button onClick={handleCreateNewCV} className="gap-2">
                <Plus className="w-4 h-4" />
                {t('dashboard.selectCV.createNew')}
              </Button>
            </div>
          )}

          {showNoResults && (
            <div className="text-center py-10">
              <p className="text-sm text-cj-green-dark/50">{t('dashboard.selectCV.noResults')}</p>
            </div>
          )}

          {!isLoading && filteredCVs.length > 0 && (
            <div className="space-y-2.5">
              {filteredCVs.map(cv => (
                <CVMiniatureSelectable
                  key={cv.id}
                  cv={cv}
                  isSelected={localSelectedCvId === cv.id}
                  onSelect={handleSelectCV}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!showEmptyState && (
          <DialogFooter className="px-6 py-4 border-t border-cj-green-dark/5 gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleConfirm} disabled={!localSelectedCvId} className="min-w-[120px]">
              {t('dashboard.selectCV.applyButton')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
